import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Login } from '@sunrun/ui-customer-login';
import { Redirect, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import withSizes from 'react-sizes';
import { useCookies } from 'react-cookie';
import config from '../../config';
import mixpanel, { aliasUser } from '../../utilities/mixpanel';
import { DASHBOARD_ROUTE } from '../../constants/routes';
import { setAuthToken, removeTokens, setRefreshToken } from '../../providers/authTokenProvider';
import { logIn } from '../../redux/modules/authentication';
import { setLoginFlowComplete } from '../../redux/modules/unsavedApp';
import { ROUTE_AFTER_LOGIN } from '../../constants/mixpanelEventNames';
import { fetchUserSuccess } from '../../redux/modules/user';

import { useIsLoggedIn } from '../../hooks/useAuthentication';
import { MIN_DESKTOP_BREAKPOINT } from '../general/constants/_breakpoints';
import { gtag } from '../../utilities/analytics';
import mapSizesToProps from '../general/utilities/mapSizesToProps';
import { ExternalChatButton } from '../support/ChatButton';
import ChatWorkingHours from '../support/ChatWorkingHours';
import PasswordlessLoginBackground from './PasswordlessLoginBackground';

const Container = styled.div`
  width: 100%;
  padding: 40px 25px;
  z-index: 12;

  @media only screen and (min-width: ${MIN_DESKTOP_BREAKPOINT}) {
    max-width: 560px;
    padding: 50px;
  }
`;

const analytics = {
  track: (title, opts) => {
    mixpanel.track(title, opts);
    gtag('event', title, {
      event_category: 'login',
      ...opts,
    });
  },
};

const PasswordlessLoginContainer = ({ isMobile }) => {
  const isLoggedIn = useIsLoggedIn();
  const dispatch = useDispatch();
  const [, setCookie, removeCookie] = useCookies(['access-token', 'refresh-token']);

  const location = useLocation();
  const [prevLocation] = useState(location?.state?.from || {});

  useEffect(() => {
    // If they are loading the login page, lets make sure there are no auth tokens stores
    // There was a previous issue where the API could return empty oppties, the FE would set these tokens
    // But not let the user login. At that point they were orphaned and unable to logout or login
    if (!isLoggedIn) {
      removeTokens();

      removeCookie('access-token');
      removeCookie('refresh-token');
    }
  }, []);

  const loginUser = data => {
    setAuthToken(data.data.accessToken);
    setRefreshToken(data.data.refreshToken);
    dispatch(setLoginFlowComplete(true));
    aliasUser(data);
    dispatch(fetchUserSuccess(data));
    dispatch(logIn(data));

    // CLIENT_HOST looks like devmaj.sunrundev.com for all but local env. For local it's "localhost"
    const domainParts = config.CLIENT_HOST.split('.');
    if (domainParts.length === 3) {
      domainParts[0] = '';
    }

    // we pass string like .sunrundev.com to share cookies across domain + all subdomains
    setCookie('access-token', data.data.accessToken, { path: '/', domain: domainParts.join('.') });
    setCookie('refresh-token', data.data.refreshToken, { path: '/', domain: domainParts.join('.') });
  };

  const findRoute = pathName => {
    if (pathName?.pathname) return `${pathName.pathname}${pathName.search}`;
    return DASHBOARD_ROUTE;
  };

  if (isLoggedIn) {
    const redirectTo = findRoute(prevLocation);
    mixpanel.track(ROUTE_AFTER_LOGIN, { route: redirectTo });
    return <Redirect push to={redirectTo} />;
  }

  return (
    <PasswordlessLoginBackground>
      <Container>
        <Login
          isServiceContractRequired
          loginUser={loginUser}
          envName={config.BUSINESS_ENV || 'local'}
          analytics={analytics}
          showNavigation={isMobile}
          ChatButton={ExternalChatButton}
          ChatWorkingHours={ChatWorkingHours}
        />
      </Container>
    </PasswordlessLoginBackground>
  );
};

PasswordlessLoginContainer.propTypes = {
  isMobile: PropTypes.bool.isRequired,
};

export default withSizes(mapSizesToProps)(PasswordlessLoginContainer);
